import React from 'react';
import PropTypes from 'prop-types';
import BootstrapTable from 'react-bootstrap-table-next';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import paginationFactory from 'react-bootstrap-table2-paginator';
import { isEmpty } from 'lodash';

import StatusBadges from '@components/badges/StatusBadges';
import TableSearchInputGroup from '@components/smartAdminUI/input/TableSearchInputGroup';
import OfficeActionButtons from './actionButtons/OfficeActionButtons';
import Button from '../smartAdminUI/Button';

const OfficesTable = ({
  displayData,
  onOfficeSelect,
  onAuditClick,
  isModal = false,
}) => {
  // NOTE: P9 mentioned that isCopiable will always be false so its not included
  const actionsFormatter = (cell, row) => (
    <OfficeActionButtons
      viewClicked={onOfficeSelect}
      auditClicked={onAuditClick}
      currRow={row}
      isEditable={row?.isEditable}
      isAuditable={row?.isAuditable}
    />
  );

  const hdrSortClasses = (_column, sortOrder) => `sorting_${sortOrder}`;

  const columns = [
    {
      dataField: 'actions',
      text: '',
      formatter: actionsFormatter,
      headerStyle: { width: '110px' },
    },
    {
      dataField: 'UniqueID',
      text: 'ID',
      hidden: true,
    },
    {
      dataField: 'OfficeMlsId',
      text: 'MLS ID',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      headerStyle: { width: '110px' },
    },
    {
      dataField: 'OfficeStatus',
      text: 'Status',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      align: 'center',
      headerStyle: { width: '70px' },
      formatter: cellContent => (isEmpty(cellContent) ? '' : <StatusBadges status={cellContent} />),
    },
    {
      dataField: 'OfficeName',
      text: 'Name',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
    },
    {
      dataField: 'OfficePhone',
      text: 'Phone',
      headerStyle: { width: '120px' },
    },
    {
      dataField: 'OfficeAOR',
      text: 'AOR',
      headerStyle: { width: '110px' },
    },
  ];

  const sizePerPageRenderer = ({
    options,
    currSizePerPage,
    onSizePerPageChange,
  }) => (
    <div className="btn-group" role="group">
      {
        options.map(option => {
          const isSelect = currSizePerPage === `${option.page}`;
          return (
            <Button
              variant={`${isSelect ? 'primary' : 'outline-primary'}`}
              key={option.text}
              onClick={() => onSizePerPageChange(option.page)}
            >
              {option.text}
            </Button>
          );
        })
      }
    </div>
  );

  const customTotal = (from, to, size) => (
    <span className="react-bootstrap-table-pagination-total">
      &nbsp;
      {`Showing rows ${from} to ${to} of ${size}`}
    </span>
  );

  const options = {
    firstPageText: '<<',
    hidePageListOnlyOnePage: true,
    lastPageText: '>>',
    nextPageText: '>',
    paginationTotalRenderer: customTotal,
    prePageText: '<',
    showTotal: true,
    sizePerPageList: [],
    sizePerPage: isModal ? 5 : 10,
    sizePerPageRenderer,
  };

  return (
    <ToolkitProvider
      columns={columns}
      data={displayData}
      keyField="UniqueID"
      search
    >
      {toolKit => (
        <>
          {displayData.length > 1 && <TableSearchInputGroup {...toolKit.searchProps} />}
          <BootstrapTable
            defaultSorted={[{ dataField: 'OfficeName', order: 'desc' }]}
            wrapperClasses="table-responsive"
            classes="dataTable"
            bordered={false}
            striped
            hover
            id="officesTable"
            noDataIndication="Your search returned no results. Please try adjusting your search criteria."
            pagination={displayData.length > 1 ? paginationFactory(options) : null}
            {...toolKit.baseProps}
          />
        </>
      )}
    </ToolkitProvider>
  );
};

OfficesTable.propTypes = {
  displayData: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  onOfficeSelect: PropTypes.func.isRequired,
  onAuditClick: PropTypes.func.isRequired,
  isModal: PropTypes.bool,
};

export default OfficesTable;
